<template>
  <div class="flex flex-col">
    <div class="flex items-center justify-between gap-4 mb-4">
      <RedesignWebccCurrencyInput
        v-model="from"
        class="w-full"
        :name="`${name}-min`"
        :currency
        :min
        :max
        :default-value="min"
        :placeholder="min.toString()"
        :disabled
        :loading
        @change="updateModel"
      >
        {{ $t(`${trPrefix}priceMin`) }}
      </RedesignWebccCurrencyInput>

      &mdash;

      <RedesignWebccCurrencyInput
        v-model="to"
        class="w-full"
        :name="`${name}-max`"
        :currency
        :min
        :default-value="max"
        :placeholder="`${max}+`"
        :disabled
        :loading
        @change="updateModel"
      >
        {{ $t(`${trPrefix}priceMax`) }}
      </RedesignWebccCurrencyInput>
    </div>

    <div class="p-4">
      <RedesignWebccHistogram
        v-model="interval"
        class="max-h-24"
        :items
        :disabled
        :loading
        :loading-items="ITEMS_SAMPLE"
      />

      <RedesignWebccSlider
        v-model="interval"
        :name
        :total-min="MIN"
        :total-max="MAX"
        :min
        :max
        :disabled
        :loading
        @change="updateModel"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
// @TODO get rid of `.newFilter.` translations as newFilter components are removed
const trPrefix = `www.components.views.search.newFilter.filterSections.price.`

type PriceOption = { value: PriceRangeValue; count?: number }

/**
 * Simulate real 26 price options.
 */
const ITEMS_SAMPLE: PriceOption[] = [
  { value: [0, 200], count: 24 },
  { value: [200, 400], count: 773 },
  { value: [400, 600], count: 2898 },
  { value: [600, 800], count: 3570 },
  { value: [800, 1000], count: 3636 },
  { value: [1000, 1200], count: 2785 },
  { value: [1200, 1400], count: 1765 },
  { value: [1400, 1600], count: 1125 },
  { value: [1600, 1800], count: 719 },
  { value: [1800, 2000], count: 509 },
  { value: [2000, 2200], count: 316 },
  { value: [2200, 2400], count: 244 },
  { value: [2400, 2600], count: 208 },
  { value: [2600, 2800], count: 148 },
  { value: [2800, 3000], count: 112 },
  { value: [3000, 3200], count: 82 },
  { value: [3200, 3400], count: 77 },
  { value: [3400, 3600], count: 49 },
  { value: [3600, 3800], count: 40 },
  { value: [3800, 4000], count: 33 },
  { value: [4000, 4200], count: 29 },
  { value: [4200, 4400], count: 28 },
  { value: [4400, 4600], count: 16 },
  { value: [4600, 4800], count: 18 },
  { value: [4800, 5000], count: 6 },
  { value: [5000, 0], count: 48 },
]
const [MIN] = ITEMS_SAMPLE.at(0)!.value
const [MAX] = ITEMS_SAMPLE.at(-1)!.value

const props = defineProps<{
  name: string
  options: PriceOption[]
  currency: string
  disabled?: boolean
  loading?: boolean
}>()

const model = defineModel<PriceRangeValue>({ required: true })

const from = ref(0)
const to = ref(0)

const interval = computed({
  get: () => [from.value, Math.min(to.value, MAX)] as NumInterval,
  set: (value) => ([from.value, to.value] = value),
})

const id = useId()
const name = computed(() => `range-${id}-${props.name}`)
const items = computed(() =>
  props.options.toSorted((a, b) => a.value[0] - b.value[0]),
)
const itemsWithCount = computed(() => items.value.filter(({ count }) => count))
const min = computed(() => itemsWithCount.value.at(0)?.value[0] ?? MIN)
const max = computed(() => itemsWithCount.value.at(-1)?.value[0] ?? MAX)

watchEffect(() => {
  from.value = model.value[0] || min.value
  to.value = model.value[1] || max.value
})

function updateModel() {
  model.value = [from.value <= min.value ? 0 : from.value, to.value]
}
</script>
